/**
 * Selecting activities and work types that may have been deactivated.
 *
 * Weld IT case 5142, R5/R6. The rule is: filter on the NEW-entry path, resolve
 * on the DISPLAY path.
 *
 *  - a new registration may only choose an active record;
 *  - an entry that already references a deactivated one keeps showing it, by
 *    name, marked "(inaktiv)" — never blank, and never silently dropped, which
 *    is what happened before the API carried the flag at all.
 *
 * Every predicate here uses `active !== false` rather than `active === true`.
 * That is deliberate: against an API build that predates the field, `active` is
 * `undefined`, and treating that as "inactive" would empty every picker in the
 * app. Missing means "cannot know", which means "allow".
 */

/** Records the user may choose for a NEW entry. */
export const isSelectable = (record) => !!record && record.active !== false

/**
 * Display label for an activity.
 *
 * `Sveising (3)` — the number disambiguates duplicates, which a tenant
 * legitimately has, since uniqueness is (name, number). Deactivated records
 * carry a suffix so nobody wonders why they cannot pick it again.
 */
export const activityLabel = (record, inactiveSuffix) => {
  if (!record) return ''
  const number = record.number === null || record.number === undefined ? '' : String(record.number).trim()
  const base = number === '' ? record.name : `${record.name} (${number})`

  return isSelectable(record) ? base : `${base} ${inactiveSuffix}`
}

/**
 * Display label for a work type — `Overtid 50 % (1050)`.
 *
 * The external salary code is what payroll keys on, and imported wage types
 * routinely share a name, so a bare name is ambiguous in exactly the way a
 * duplicate activity name is. Records without a code render unchanged.
 *
 * Both payload spellings are accepted: the Timer's list sends
 * `external_salary_code`, the settings/admin payload `externalSalaryCode`.
 */
export const workTypeLabel = (record, inactiveSuffix) => {
  if (!record) return ''

  const rawCode = record.external_salary_code === undefined
    ? record.externalSalaryCode
    : record.external_salary_code
  const code = rawCode === null || rawCode === undefined ? '' : String(rawCode).trim()
  const base = code === '' ? record.name : `${record.name} (${code})`

  return isSelectable(record) ? base : `${base} ${inactiveSuffix}`
}

/**
 * The options one picker should offer.
 *
 * Active records, plus — pinned in — whichever record the entry being edited
 * already points at, even when that record is inactive. Without the pin the
 * select would have no option matching its own value and would render blank,
 * which reads as "no activity" on an entry that certainly has one.
 *
 * @param {Array}  records        full list, including inactive ones
 * @param {*}      currentId      the value the entry currently holds, or null for a new entry
 * @param {string} idKey          which field is the option value ('task_id', 'id', …)
 * @param {Function} labelFn      how to render one record
 * @param {string} inactiveSuffix e.g. "(inaktiv)"
 */
export const selectableOptions = (records, currentId, idKey, labelFn, inactiveSuffix) => {
  const list = Array.isArray(records) ? records : []
  const options = list
    .filter(isSelectable)
    .map((r) => ({ ...r, display_name: labelFn(r, inactiveSuffix) }))

  if (currentId === null || currentId === undefined || currentId === '') return options
  if (options.some((r) => r[idKey] === currentId)) return options

  const current = list.find((r) => r[idKey] === currentId)
  if (current) {
    // Pinned first so it is visible without scrolling a long list.
    return [{ ...current, display_name: labelFn(current, inactiveSuffix) }, ...options]
  }

  // Referenced but not in the list at all — the record was hard-deleted, or the
  // list is scoped so it cannot appear. Show the id rather than an empty box.
  return [{ [idKey]: currentId, display_name: String(currentId), active: false }, ...options]
}
